import {
  type ChangeRequest,
  type ChangeRequestsView,
  type CreateChangeRequest,
  type ReviewActions,
  type ReviewView,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { refreshLists } from '../../lib/list';

/** Underwriters and tenant admins review; auditors read the review. */
export function useReviewAccess() {
  const { tenant } = useSession();
  return { read: can(tenant, 'reviews.view'), write: can(tenant, 'reviews.edit') };
}

const key = (t: string | undefined, id: string) => ['assessments', t, id] as const;

export function useReview(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id, id), 'review'],
    queryFn: () => api<ReviewView>('GET', `/assessments/${id}/review`),
    enabled: !!tenant && enabled,
  });
}

export function useChangeRequests(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id, id), 'change-requests'],
    queryFn: () => api<ChangeRequestsView>('GET', `/assessments/${id}/change-requests`),
    enabled: !!tenant && enabled,
  });
}

/** Every review change reloads the assessment, its review and the lists it shows in. */
function useReviewMutation<I, O>(id: string, fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: key(tenant?.id, id) }),
        qc.invalidateQueries({ queryKey: ['assessments', tenant?.id], exact: true }),
        refreshLists(qc),
      ]),
  });
}

export const useReviewActions = (id: string) =>
  useReviewMutation(id, (body: ReviewActions) => api<{ recorded: number }>('POST', `/assessments/${id}/review/actions`, body)); // prettier-ignore
export const useStartReview = (id: string) =>
  useReviewMutation(id, () => api('POST', `/assessments/${id}/review/start`));
export const useSendToBusiness = (id: string) =>
  useReviewMutation(id, () => api('POST', `/assessments/${id}/review/send`));
export const useRequestChange = (id: string) =>
  useReviewMutation(id, (body: CreateChangeRequest) => api<ChangeRequest>('POST', `/assessments/${id}/change-requests`, body)); // prettier-ignore
export const useWithdrawRequest = (id: string) =>
  useReviewMutation(id, (rid: string) => api('POST', `/assessments/${id}/change-requests/${rid}/withdraw`));
export const useReplyToRequest = (id: string) =>
  useReviewMutation(id, ({ rid, text }: { rid: string; text: string }) => api('POST', `/assessments/${id}/change-requests/${rid}/reply`, { text })); // prettier-ignore
export const useSendBack = (id: string) =>
  useReviewMutation(id, () => api('POST', `/assessments/${id}/send-back`));
